import { useState, useRef, useEffect, useMemo } from 'react';
import { sendContractModal } from '../modal';
import { SearchableSelect } from '.';
import { useStore } from '../../context';
import { filterYardOptions, useSaveFilters, useYardOptionsQuery } from '../../hooks';
import { showToRoles } from '../../utils';
import { belongsToOptions, clientYardService } from '../../services';
import { i18n } from '../../i18n';

export const FILTERS_TYPES = ['marketplace', 'managesales', 'sales-fees', 'overview'] as const;
const hideToFilterTypes = (types: (typeof FILTERS_TYPES)[number][], filterType?: string) => {
  return !types.some((val) => val === filterType);
};

interface BelongsToFilterSelectsProps {
  handleChange: (obj: any) => void;
  isYardRequired?: boolean;
  isClientRequired?: boolean;
  isSubmitClicked?: boolean;
  filtersType?: (typeof FILTERS_TYPES)[number];
  withoutLabel?: boolean;
  /** Не перезапрашивать yard options после первой загрузки (например, при invalidate cache) */
  onceRequest?: boolean;
}

export const BelongsToFilterSelects: React.FC<BelongsToFilterSelectsProps> = ({
  handleChange,
  isYardRequired,
  isClientRequired,
  isSubmitClicked,
  filtersType,
  withoutLabel,
  onceRequest,
}: BelongsToFilterSelectsProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [oldFilters, setOldFilters] = useSaveFilters('belongs-to', true);
  const [, setYardCompanyName] = useState(oldFilters.yardCompanyName);
  const yardCompanyId = useRef(oldFilters.yardCompanyId);
  const [country, setCountry] = useState(oldFilters.country);
  const [city, setCity] = useState(oldFilters.city);
  const [yardName, setYardName] = useState(oldFilters.yardName);
  const yardId = useRef(oldFilters.yardId);
  const [clientName, setClientName] = useState(oldFilters.clientName);
  const clientId = useRef(oldFilters.clientId);

  const yardQuerySnapshot = useRef({ country: [] as any[], city: [] as any[], yard: [] as any[] });
  const [isOptionsFrozen, setIsOptionsFrozen] = useState(false);
  const isYardCompanyUser = showToRoles(
    ['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
    user,
  );
  const hasYardOptionsOwnerId =
    (!isYardCompanyUser && !showToRoles('client', user)) ||
    (isYardCompanyUser && !!yardCompanyId.current) ||
    (showToRoles('client', user) && !!clientId.current);

  const { data: yardQueryLive } = useYardOptionsQuery(
    [
      'yard-short',
      'assigned-yards' +
        (yardCompanyId.current &&
        showToRoles(
          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
          user,
        )
          ? yardCompanyId.current
          : clientId.current
          ? clientId.current
          : ''),
    ],
    {
      user,
      yardCompanyId: yardCompanyId.current,
      clientId: clientId.current,
      country,
      city,
    },
    onceRequest
      ? {
          enabled: !isOptionsFrozen && hasYardOptionsOwnerId,
          onSuccess: (data: any) => {
            yardQuerySnapshot.current = data;
            setIsOptionsFrozen(true);
          },
        }
      : undefined,
  );

  const yardQuery = useMemo(() => {
    if (onceRequest && isOptionsFrozen) {
      return filterYardOptions(yardQuerySnapshot.current, country, city);
    }

    return yardQueryLive;
  }, [city, country, isOptionsFrozen, onceRequest, yardQueryLive]);

  useEffect(() => {
    if (
      showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'], user) &&
      !oldFilters.yardCompanyId
    ) {
      const yardCompanyObj = {
        label: showToRoles(
          ['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
          user,
        )
          ? user.yardCompanyEmployee.yardCompany.legalName
          : '',
        id: showToRoles(
          ['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
          user,
        )
          ? user.yardCompanyEmployee.yardCompanyId
          : '',
      };
      handleYardCompanySelect(yardCompanyObj);
    }

    if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user) && !oldFilters.yardId) {
      const yardObj = {
        label: showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)
          ? user.yardEmployee.yard.name
          : '',
        id: showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user) ? user.yardEmployee.yardId : '',
      };
      handleYardSelect(yardObj);
    }

    if (showToRoles('client', user) && !oldFilters.clientId) {
      const clientObj = {
        label: showToRoles('client', user) ? user.clientEmployee.client.legalName : '',
        id: showToRoles('client', user) ? user.clientEmployee.clientId : '',
      };
      handleClientSelect(clientObj);
    }
  }, []);

  const handleYardCompanySelect = (obj: any) => {
    yardCompanyId.current = obj.id;
    clientId.current = '';
    setYardCompanyName(obj.label);
    setCountry('');
    setCity('');
    setYardName('');
    setClientName('');

    const data = {
      yardCompanyId: obj.id,
      country: '',
      city: '',
      yardId: '',
      clientId: '',
    };

    setOldFilters({
      ...oldFilters,
      yardCompanyName: obj.label,
      ...data,
      clientName: '',
      yardName: '',
    });
    if (!showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'], user))
      handleChange && handleChange(data);
  };

  const handleCountrySelect = (obj: any) => {
    const otherData: any = {};
    if (
      showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
        user,
      )
    ) {
      otherData.clientId = '';
      clientId.current = '';
      setCity('');
      setYardName('');
      setClientName('');
    }
    if (showToRoles(['CLIENT_MANAGER', 'CLIENT_DISPATCHER'], user)) {
      setCity('');
      setYardName('');
    }

    setCountry(obj.label);

    const data = { country: obj.label, city: '', yardId: '', ...otherData };
    setOldFilters({ ...oldFilters, ...data, clientName: '', clientId: '' });
    if (!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) handleChange && handleChange(data);
  };

  const handleCitySelect = (obj: any) => {
    const otherData: any = {};
    if (
      showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
        user,
      )
    ) {
      otherData.clientId = '';
      clientId.current = '';
      setYardName('');
      setClientName('');
    }
    if (showToRoles(['CLIENT_MANAGER', 'CLIENT_DISPATCHER'], user)) {
      setYardName('');
    }

    setCity(obj.label);

    const data = { city: obj.label, yardId: '', ...otherData };
    setOldFilters({ ...oldFilters, ...data, clientName: '' });
    if (!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) handleChange && handleChange(data);
  };

  const handleYardSelect = (obj: any) => {
    if (
      obj.isNew &&
      showToRoles('client', user) &&
      hideToFilterTypes(['marketplace', 'managesales'], filtersType)
    ) {
      sendContractModal.yardId = obj.id;
      sendContractModal.open();
      return;
    }

    const otherData: any = {};
    if (
      showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
        user,
      )
    ) {
      otherData.clientId = '';
      clientId.current = '';
      setClientName('');
    }

    yardId.current = obj.id;
    setYardName(obj.label);

    const data = { yardId: obj.id, ...otherData };
    setOldFilters({ ...oldFilters, yardName: obj.label, ...data, clientName: '' });
    if (!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) handleChange && handleChange(data);
  };

  const handleClientSelect = (obj: any) => {
    clientId.current = obj.id;
    setClientName(obj.label);

    const data = { clientId: obj.id };
    setOldFilters({ ...oldFilters, clientName: obj.label, ...data });
    if (!showToRoles('client', user)) handleChange && handleChange(data);
  };

  useEffect(() => {
    if (
      !isYardRequired ||
      yardId.current ||
      yardQuery.yard.length !== 1 ||
      !showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'], user)
    )
      return;

    handleYardSelect(yardQuery.yard[0]);
  }, [isYardRequired, user.type, yardQuery.yard]);

  return (
    <>
      {!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user) &&
        hideToFilterTypes(['managesales', 'overview'], filtersType) && (
          <SearchableSelect
            selectedOptionLabel={country}
            label={withoutLabel ? '' : i18n.t('label.country')}
            onSelect={handleCountrySelect}
            options={yardQuery.country}
            selectedAreasEqaul={{ key: 'id', keys: [country] }}
            withClearButton
          />
        )}
      {!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user) &&
        hideToFilterTypes(['managesales', 'overview'], filtersType) && (
          <SearchableSelect
            selectedOptionLabel={city}
            label={withoutLabel ? '' : i18n.t('label.city')}
            onSelect={handleCitySelect}
            options={yardQuery.city}
            selectedAreasEqaul={{ key: 'id', keys: [city] }}
            withClearButton
          />
        )}
      {!showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user) && (
        <SearchableSelect
          selectedOptionLabel={yardName}
          label={withoutLabel ? '' : i18n.t('common.yard') + (isYardRequired ? '*' : '')}
          onSelect={handleYardSelect}
          options={yardQuery.yard}
          selectedAreasEqaul={{ key: 'id', keys: [yardName] }}
          withClearButton={hideToFilterTypes(['overview'], filtersType)}
          error={isYardRequired && isSubmitClicked && !yardId.current}
        />
      )}
      {showToRoles('adminRead', user) &&
        hideToFilterTypes(['sales-fees', 'overview'], filtersType) && (
          <SearchableSelect
            selectedOptionLabel={clientName}
            label={withoutLabel ? '' : i18n.t('common.client') + (isClientRequired ? '*' : '')}
            onSelect={handleClientSelect}
            queryFn={() => {
              if (
                (showToRoles(
                  ['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
                  user,
                ) ||
                  !!yardCompanyId.current) &&
                !yardId.current
              )
                return () => [];

              if (yardId.current)
                return clientYardService.assignedClients({
                  id: yardId.current,
                });

              if (showToRoles(['SUPER_ADMIN'], user)) return belongsToOptions.Client('', true);

              return () => [];
            }}
            queryKey={['clients', 'assigned-clients' + yardId.current]}
            selectedAreasEqaul={{ key: 'id', keys: [clientId.current] }}
            disabled={
              (showToRoles(
                ['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
                user,
              ) ||
                !!yardCompanyId.current) &&
              !yardId.current
            }
            withClearButton
            error={isClientRequired && isSubmitClicked && !clientId.current}
          />
        )}
    </>
  );
};
